import React, { useState, useEffect, useContext } from 'react';
import {
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  TypingIndicator,
  ConversationHeader,
  Avatar,
} from '@chatscope/chat-ui-kit-react';
import {
  Row,
  Col,
  Space,
  Card,
  Form,
  Button,
  Typography,
  FloatButton,
  Input,
  message,
  Skeleton,
  Switch,
} from 'antd';
import {
  CustomInput,
  CustomTextArea,
  CustomSelect,
  CustomColorPicker,
} from 'components/custom';
import {
  getChatInterfaceSettings,
  saveChatInterfaceSettings,
  uploadFile,
} from 'api-services/chat-interface';
import { BotIconClose, MinimizeIcon } from '../../../assets/icons';
//import BotImage from '../../../assets/img/BotImage.svg';
import ChatBubbleIcon from '../../../assets/img/chat-bubble-icon.svg';
import { CHAT_INTERFACE } from 'utilities/constants';
// Style
import './scss/chat-interface-overrides.scss';
import useLoading from 'hooks/use-loading';
import ChatBotContext from 'hooks/chat-bot-context';
import { useNavigate } from 'react-router';

const { Paragraph } = Typography;

function ChatInterfaceSettings() {
  const [form] = Form.useForm();

  const navigate = useNavigate();
  const { selectedBot, botName } = useContext(ChatBotContext);
  const { loading, setLoading } = useLoading();

  const [skeleton, setSkeleton] = useState(true);
  const [requiredMark, setRequiredMarkType] = useState('optional');

  const [initialMessages, setInitialMessages] = useState(
    'Hi! What can I help you with?\nWhat are you looking for?\nHow can I be of your help?'
  );
  const [suggestedMessages, setSuggestedMessages] = useState(
    'I need info about the product?\nHow do I claim a refund of my order?\nHow soon can I get my money back?'
  );
  const [chatbotTheme, setChatbotTheme] = useState('#FFFFFF');
  const [chatbotPicture, setChatbotPicture] = useState(null);
  const [removeChatbotPic, setRemoveChatbotPic] = useState(false);
  const [collectUserInfo, setCollectUserInfo] = useState(false);
  const [displayName, setDisplayName] = useState('Company Name');
  const [autoShowPopup, setautoShowPopup] = useState(0);
  const [userMessageColor, setUserMessageColor] = useState('#EDF5FE');
  const [fontColor, setFontColor] = useState('#FFFFFF');
  const [chatIcon, setChatIcon] = useState(null);
  const [removeChatIcon, setRemoveChatIcon] = useState(false);
  const [chatBubbleColor, setChatBubbleColor] = useState('#6754E2');
  const [chatBubbleAlignment, setChatBubbleAlignment] = useState('right');

  const [isChatContainerOpen, setChatContainerOpen] = useState(true);
  const [title, setTitle] = useState('ecommerce');
  const [isTyping] = useState(false);
  const [botImage, setBotImage] = useState('');
  const [, setUserImage] = useState('');

  const [messages, setMessages] = useState([]);
  const [, setOptionMessages] = useState([]);
  const [chatProfileFile, setChatProfileFile] = useState(null);
  const [chatProfileName, setChatProfileName] = useState(null);
  const [chatIconName, setChatIconName] = useState(null);
  const [chatIconFile, setChatIconFile] = useState(null);

  useEffect(() => {
    fetchChatInterfaceSettings();
    setBotImage(CHAT_INTERFACE.BOT_IMAGE);
    setUserImage(CHAT_INTERFACE.USER_IMAGE);
  }, [selectedBot]);

  const fetchChatInterfaceSettings = async () => {
    if (selectedBot) {
      setSkeleton(true);
      const response = await getChatInterfaceSettings(selectedBot);
      if (response?.data) {
        const {
          initial_messages,
          suggested_message,
          profile_picture_path,
          user_message_color_code,
          chat_icon_path,
          chat_bubble_button_color,
          chat_bubble_button_alignment,
          chat_icon_file_name,
          profile_picture_file_name,
          theme_hex_color,
          font_hex_color,
          remove_profile_picture,
          remove_chat_icon,
          show_messages_after,
          display_name,
          take_user_info,
        } = response.data;
        setRemoveChatIcon(remove_chat_icon);
        setRemoveChatbotPic(remove_profile_picture);
        setCollectUserInfo(take_user_info ?? false);
        setInitialMessages(initial_messages);
        setSuggestedMessages(suggested_message);
        if (show_messages_after) setautoShowPopup(show_messages_after);
        setChatbotTheme(theme_hex_color ?? '#FFFFFF');
        setChatbotPicture(profile_picture_path);
        setDisplayName(display_name ?? botName);
        setUserMessageColor(user_message_color_code);
        setChatIcon(chat_icon_path);
        setChatBubbleColor(chat_bubble_button_color ?? chatBubbleColor);
        setChatBubbleAlignment(chat_bubble_button_alignment ?? 'right');
        setChatIconName(chat_icon_file_name);
        setChatProfileName(profile_picture_file_name);
        setFontColor(font_hex_color);
      }
    } else {
      navigate('/chatbots');
    }
    setSkeleton(false);
  };
  const createMessage = (msg, index, userType, formattedTime) => ({
    id: index + 1,
    sender: {
      userType,
    },
    content: msg,
    timestamp: formattedTime,
  });

  useEffect(() => {
    setTitle(displayName);

    const msgsArr = initialMessages.split('\n');
    const msgsListConverted = msgsArr
      .filter((msg) => msg.length > 0)
      .map((msg, index) => createMessage(msg, index, 'Bot'));
    const userMessage = createMessage(
      'Hi there. Quick question! How to you select products across multiple categories and add it to your cart?',
      msgsListConverted.length,
      'User'
    );
    setMessages([...msgsListConverted, userMessage]);

    const suggestedMsgsArr = suggestedMessages.split('\n');
    const suggestedMsgsListConverted = suggestedMsgsArr
      .filter((suggestedMsg) => suggestedMsg.length > 0)
      .map((suggestedMsg, index) => createMessage(suggestedMsg, index, 'User'));
    setOptionMessages([...suggestedMsgsListConverted]);
  }, [displayName, initialMessages, suggestedMessages]);

  const handleChatContainerOpen = (isOpen) => {
    setChatContainerOpen(!isOpen);
  };

  const onRequiredTypeChange = ({ requiredMarkValue }) => {
    setRequiredMarkType(requiredMarkValue);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      let pictureProfileUrl;
      let iconUrl;
      if (chatProfileFile) {
        const formData = new FormData();
        formData.append('logo', chatProfileFile);
        formData.append('logo_type', 'profile_picture');
        const response = await uploadFile(selectedBot, formData);
        pictureProfileUrl = response.data.file_path;
      }
      if (chatIconFile) {
        const formData = new FormData();
        formData.append('logo', chatIconFile);
        formData.append('logo_type', 'chat_icon');
        const response = await uploadFile(selectedBot, formData);
        iconUrl = response.data.file_path;
      }

      // Send API request and receive response
      const payload = {
        remove_profile_picture: removeChatbotPic,
        take_user_info: collectUserInfo,
        initial_messages: initialMessages,
        suggested_messages: suggestedMessages,
        // theme: chatbotTheme,
        theme_hex_color: chatbotTheme,
        profile_picture_path: removeChatbotPic
          ? null
          : chatProfileFile
            ? pictureProfileUrl
            : chatbotPicture,
        //bot_name: displayName,
        display_name: displayName,
        user_message_color_code: userMessageColor,
        font_hex_color: fontColor,
        remove_chat_icon: removeChatIcon,
        chat_icon_path: removeChatIcon
          ? null
          : chatIconFile
            ? iconUrl
            : chatIcon,
        chat_bubble_button_color: chatBubbleColor,
        chat_bubble_button_alignment: chatBubbleAlignment,
        show_messages_after: autoShowPopup,
      };

      const response = await saveChatInterfaceSettings(selectedBot, payload);
      if (response?.data?.success) {
        message.destroy();
        message.success('Widget settings saved successfully');
        // Process the response data and update the chat with the response message
        setLoading(false);
      } else {
        // Handle error if the API request is unsuccessful
        setLoading(false);
        console.error(
          'API request failed:',
          response.status,
          response.statusText
        );
      }
    } catch (error) {
      // Handle error if the API request encounters an exception
      console.error('API request error:', error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const fileReader = (event, state, fileState, setFileName) => {
    const file = event.target.files[0]; // Get the first selected file
    if (file) {
      const fileNamePart = file.name.split('.');
      const extension = fileNamePart[fileNamePart.length - 1];

      if (!['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
        message.destroy();
        message.error('Please upload valid file extesion like jpg and png etc');
        return;
      }

      fileState(file);
      setFileName(file.name);
      const reader = new FileReader();

      reader.onload = (e) => {
        const previewUrl = e.target.result;
        state(previewUrl);
      };

      reader.readAsDataURL(file);
    }
  };
  const handleChatProfileChange = (event) => {
    fileReader(
      event,
      setChatbotPicture,
      setChatProfileFile,
      setChatProfileName
    );
  };

  const handleChatIconChange = (event) => {
    fileReader(event, setChatIcon, setChatIconFile, setChatIconName);
  };

  const iconDisplay = removeChatIcon ? 'none' : 'block';

  return (
    <div className="chatbot-wrapper">
      <Row>
        <Col span={18}>
          <h1 className="page-title">Widget Settings</h1>

          <Space size="middle" direction="vertical" style={{ display: 'flex' }}>
            {skeleton && (
              <Card>
                <Skeleton active />
              </Card>
            )}
            {!skeleton && (
              <Card>
                <Form
                  id="chat-interface"
                  form={form}
                  layout="vertical"
                  initialValues={{
                    requiredMarkValue: requiredMark,
                    alignChatBubbleButton: { chatBubbleAlignment },
                  }}
                  onValuesChange={onRequiredTypeChange}
                  requiredMark={requiredMark}
                >
                  <div className="card-header">
                    <div className="card-header-title">
                      <h3>Chat Interface Settings</h3>
                      <p>Applies when embedded on website</p>
                    </div>
                    <Form.Item>
                      <Space>
                        <Button
                          type="primary"
                          loading={loading}
                          disabled={loading}
                          onClick={handleSubmit}
                        >
                          Save
                        </Button>
                        <Button onClick={fetchChatInterfaceSettings}>
                          Reset All
                        </Button>
                      </Space>
                    </Form.Item>
                  </div>
                  <Row gutter={24}>
                  
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item label="Display Name" required>
                        <CustomInput
                          placeholder="Enter your chatbot name here"
                          name="displayName"
                          state={displayName}
                          setState={setDisplayName}
                        />
                      </Form.Item>
                    </Col>
                   
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        label="Update chatbot profile picture"
                        required
                      >
                        <div className="upload_file">
                          <Input
                            type="file"
                            id="profile-input"
                            style={{
                              display: 'none',
                            }}
                            onChange={handleChatProfileChange}
                          />
                          <label>
                            {chatProfileName
                              ? chatProfileName
                              : 'No file selected'}
                          </label>
                          <Button
                            type="primary"
                            onClick={() =>
                              document.getElementById('profile-input').click()
                            }
                          >
                            Upload
                          </Button>
                        </div>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item label="Update chat icon" required>
                        <div className="upload_file">
                          <Input
                            type="file"
                            id="chat-input"
                            style={{
                              display: 'none',
                            }}
                            onChange={handleChatIconChange}
                          />
                          <label>
                            {chatIconName ? chatIconName : 'No file selected'}
                          </label>
                          <Button
                            type="primary"
                            onClick={() =>
                              document.getElementById('chat-input').click()
                            }
                          >
                            Upload
                          </Button>
                        </div>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        label="Collect user information at the beginning of chat"
                        required
                      >
                        <Switch
                          onChange={setCollectUserInfo}
                          checked={collectUserInfo}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        label="Remove chatbot profile picture"
                        required
                        style={{ marginBlockEnd: 15 }}
                      >
                        <Switch
                          onChange={setRemoveChatbotPic}
                          checked={removeChatbotPic}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        label="Remove chat icon"
                        required
                        style={{ marginBlockEnd: 15 }}
                      >
                        <Switch
                          onChange={setRemoveChatIcon}
                          checked={removeChatIcon}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        label="User Message Color"
                        required
                        style={{ marginBlockEnd: 5 }}
                      >
                        <CustomColorPicker
                          state={userMessageColor}
                          setState={setUserMessageColor}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        label="Font Color"
                        required
                        style={{ marginBlockEnd: 5 }}
                      >
                        <CustomColorPicker
                          state={fontColor}
                          setState={setFontColor}
                        />
                      </Form.Item>
                    </Col>
                
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item label="Chat Bubble Button Color" required>
                        <CustomColorPicker
                          state={chatBubbleColor}
                          setState={setChatBubbleColor}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item label="Theme" name="theme_hex_color" required>
                        <CustomSelect
                          name="chatbotTheme"
                          state={chatbotTheme}
                          defaultValue={chatbotTheme}
                          setState={setChatbotTheme}
                          options={CHAT_INTERFACE.THEME_OPTION}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        label="Align Chat Bubble Button"
                        required
                        style={{ marginBlockEnd: 5 }}
                        name="alignChatBubbleButton"
                      >
                        <CustomSelect
                          state={chatBubbleAlignment}
                          setState={setChatBubbleAlignment}
                          style={{ width: '100%' }}
                          defaultValue={chatBubbleAlignment}
                          options={CHAT_INTERFACE.CHAT_BUBBLE_ALIGNMENT_OPTIONS}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        label="Auto Show Initial Messages Pop-ups After"
                        required
                        style={{ marginBlockEnd: 5 }}
                      >
                        <CustomInput
                          placeholder="3"
                          name="autoShowPopup"
                          state={autoShowPopup}
                          setState={setautoShowPopup}
                        />
                        <Paragraph type="info">Negative to Disable</Paragraph>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        label="Initial Messages"
                        name="initialMessages"
                        required
                      >
                        <>
                          <CustomTextArea
                            rows={4}
                            placeholder="Hi! What can I help you with?"
                            state={initialMessages}
                            setState={setInitialMessages}
                          />
                          <Paragraph type="info">
                            Enter each message in a new line.
                          </Paragraph>
                        </>
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Paragraph type="info">
                        **If the changes here don&apos;t show up immediately on
                        your website try clearing your browser cache or use
                        incognito. New users will see the changes
                        immediately**
                      </Paragraph>
                    </Col>
                  </Row>


                </Form>
              </Card>
            )}
          </Space>
        </Col>

        {!skeleton && (
          <Col span={6} className="chatbot-viewer">
            {isChatContainerOpen ? (
              <ChatContainer
                className={`chatContainerDiv ${
                  chatbotTheme === '#FFFFFF' ? 'light' : 'dark'
                }`}
              >
                {/* background none is required for the border of chat container to be displayed correctly */}
                <ConversationHeader style={{ background: 'none' }}>
                  <ConversationHeader.Content
                    userName={title || 'Anonymous'}
                    info="powered by Folio3 AI"
                  />
                  <ConversationHeader.Actions>
                    <Button
                      icon={<MinimizeIcon color={chatBubbleColor} />}
                      onClick={() => setChatContainerOpen(false)}
                    />
                  </ConversationHeader.Actions>
                  {!removeChatbotPic && (
                    <Avatar
                      className="botAvatar"
                      size="md"
                      src={chatbotPicture ?? botImage}
                      name="AI BOT"
                    />
                  )}
                </ConversationHeader>
                <MessageList
                  typingIndicator={
                    isTyping && <TypingIndicator content="Bot is typing" />
                  }
                >
                  {messages.map((message) => (
                    <Message
                      key={message.id}
                      model={{
                        message: message.content,
                        sentTime: message.timestamp,
                        sender: message.sender.userType,
                        direction:
                          message.sender.userType === 'Bot'
                            ? 'incoming'
                            : 'outgoing',
                      }}
                      style={{
                        '--user-message-color':
                          message.sender.userType === 'Bot'
                            ? 'lightgrey'
                            : userMessageColor,
                        '--user-font-color': fontColor,
                      }}
                    >
                      {message.sender.userType === 'Bot' && (
                        <Avatar
                          className="botAvatar"
                          src={chatbotPicture ?? botImage}
                          name={message.sender.userType}
                          style={{display: iconDisplay}}
                        />
                      )}
                    </Message>
                  ))}

                  {/* {optionMessages.map((message, index) => (
                                    <Message
                                        key={index}
                                        model={{
                                            message: message.content,
                                            sentTime: message.timestamp,
                                            sender: message.sender.userType,
                                            direction: "outgoing",
                                            position: "last"
                                        }}
                                        // onClick={(e) => handleMessage(e.target.innerText)}
                                    />
                                ))} */}
                </MessageList>

                <MessageInput
                  placeholder="Type your message..."
                  attachButton={false}
                  sendOnReturnDisabled={false}
                  disabled={isTyping}
                  sendDisabled
                  style={{
                    '--user-font-color': fontColor,
                    '--chat-bubble-color': chatBubbleColor,
                  }}
                  // attachButton
                  // onAttachClick={} // write code to open file selector
                  // onSend={handleMessage}
                />
              </ChatContainer>
            ) : (
              <></>
            )}

            <div
              className={`floating-btn-container ${
                chatBubbleAlignment === 'left' ? 'float-icon-left' : ''
              }`}
            >
              <FloatButton
                className="antd-floating-button-chatbot"
                style={{
                  '--chat-bubble-color': chatBubbleColor,
                  width: 80,
                  height: 80,
                }}
                icon={
                  isChatContainerOpen ? (
                    <BotIconClose />
                  ) : !removeChatIcon ? (
                    chatIcon ? (
                      <img src={chatIcon} />
                    ) : (
                      <img src={ChatBubbleIcon} />
                    )
                  ) : (
                    <img src={ChatBubbleIcon} />
                  )
                }
                onClick={() => handleChatContainerOpen(isChatContainerOpen)}
              />
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
}

export default ChatInterfaceSettings;
